import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Save, Plus, FileText, Loader2, ExternalLink, EyeOff, Eye } from "lucide-react";
import api from "../../lib/api";

const CATEGORIES = [
  { value: "handout", label: "Handout" },
  { value: "rezept", label: "Rezept" },
  { value: "flyer", label: "Flyer" },
  { value: "poster", label: "Poster" },
  { value: "anleitung", label: "Anleitung" },
];

export default function DownloadsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/downloads");
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Konnte Downloads nicht laden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Diesen Download wirklich löschen?")) return;
    await api.delete(`/admin/downloads/${id}`);
    toast.success("Gelöscht");
    await load();
  };

  const togglePublish = async (item) => {
    await api.put(`/admin/downloads/${item.id}`, { published: !item.published });
    await load();
  };

  return (
    <div className="space-y-4" data-testid="downloads-admin-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl">Downloads</h2>
          <p className="text-sm opacity-70">PDFs, Flyer, Rezept-Karten und Anleitungen zum kostenlosen Download für Website-Besucher.</p>
        </div>
        <button onClick={() => setShowNew(true)} className="btn-primary" data-testid="downloads-new-btn">
          <Plus className="w-4 h-4" /> Neuer Download
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm opacity-70">
          <Loader2 className="w-4 h-4 animate-spin" /> Lädt …
        </div>
      ) : items.length === 0 ? (
        <div className="p-8 rounded-2xl bg-[var(--wp-cream)] text-center text-sm opacity-70">
          Noch keine Downloads. Klicke oben rechts auf {'"'}Neuer Download{'"'}.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <div
              key={it.id}
              className="plant-card p-4 grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto_auto] gap-3 sm:gap-4 items-center"
              data-testid={`downloads-row-${it.id}`}
              style={!it.published ? { opacity: 0.6 } : {}}
            >
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="w-5 h-5 shrink-0" style={{ color: "var(--wp-olive)" }} />
                <div className="min-w-0">
                  <div className="font-medium truncate">{it.title}</div>
                  {it.description && <div className="text-xs opacity-60 truncate">{it.description}</div>}
                  <div className="text-[10px] opacity-50 mt-0.5">
                    <span className="px-1.5 py-0.5 rounded bg-[var(--wp-cream)]">{it.category}</span>
                    <span className="ml-2">Reihenfolge: {it.order}</span>
                  </div>
                </div>
              </div>
              <div
                className="text-xs font-semibold tabular-nums px-2.5 py-1 rounded-full text-center"
                style={{ backgroundColor: "var(--wp-cream)", color: "var(--wp-primary)", minWidth: 64 }}
                title="Anzahl Downloads"
                data-testid={`downloads-count-${it.id}`}
              >
                ↓ {it.download_count || 0}
              </div>
              <a href={it.file_url} target="_blank" rel="noopener noreferrer" className="btn-secondary text-xs !py-1.5 !px-2" title="Datei ansehen">
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <button onClick={() => togglePublish(it)} className="btn-secondary text-xs !py-1.5 !px-2" title={it.published ? "Verstecken" : "Veröffentlichen"}>
                {it.published ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              </button>
              <div className="flex gap-1">
                <button onClick={() => setEditing(it)} className="btn-secondary text-xs !py-1.5 !px-2">Bearbeiten</button>
                <button onClick={() => remove(it.id)} className="btn-secondary text-xs !py-1.5 !px-2 text-red-600" data-testid={`downloads-delete-${it.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <DownloadDialog
          onClose={() => setShowNew(false)}
          onSaved={async () => { setShowNew(false); await load(); }}
        />
      )}
      {editing && (
        <DownloadDialog
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

function DownloadDialog({ initial, onClose, onSaved }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [category, setCategory] = useState(initial?.category || "handout");
  const [order, setOrder] = useState(initial?.order || 0);
  const [published, setPublished] = useState(initial?.published !== false);
  const [fileUrl, setFileUrl] = useState(initial?.file_url || "");
  const [fileName, setFileName] = useState(initial?.file_name || "");
  const [fileSize, setFileSize] = useState(initial?.file_size || 0);
  const [contentType, setContentType] = useState(initial?.content_type || "application/pdf");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.size || f.size <= 0) {
      toast.error("Datei ist leer oder konnte nicht gelesen werden (iOS-Foto?). Bitte erneut auswählen.");
      return;
    }
    setUploading(true);
    setUploadProgress(0);

    const CHUNK_SIZE = 4 * 1024 * 1024;    // 4 MB per chunk
    const DIRECT_LIMIT = 8 * 1024 * 1024;  // <= 8 MB → single POST (more reliable on iOS Safari)
    const totalChunks = Math.ceil(f.size / CHUNK_SIZE);

    // Direct single-shot upload (small files / iOS photos)
    const runDirect = async () => {
      const fd = new FormData();
      fd.append("file", f, f.name);
      const r = await api.post("/admin/downloads/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
        onUploadProgress: (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 95));
        },
      });
      return r;
    };

    // Chunk with retries + auto session-restart
    const sendChunk = async (uploadId, index) => {
      const start = index * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, f.size);
      const blob = f.slice(start, end);
      let lastErr = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const fd = new FormData();
          fd.append("upload_id", uploadId);
          fd.append("chunk_index", String(index));
          fd.append("chunk", blob, `chunk-${index}`);
          return await api.post("/admin/downloads/upload-chunk", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 90000,
          });
        } catch (err) {
          lastErr = err;
          const status = err?.response?.status;
          if (status === 410 || status === 404) throw err;
          await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
        }
      }
      throw lastErr;
    };

    const runChunked = async () => {
      const initR = await api.post("/admin/downloads/upload-init", {
        filename: f.name, total_size: f.size, content_type: f.type || "application/octet-stream",
      });
      const uploadId = initR.data?.upload_id;
      if (!uploadId) throw new Error("upload_id fehlt");
      for (let i = 0; i < totalChunks; i++) {
        await sendChunk(uploadId, i);
        setUploadProgress(Math.round(((i + 1) / totalChunks) * 90));
      }
      setUploadProgress(92);
      return await api.post("/admin/downloads/upload-finalize",
        { upload_id: uploadId }, { timeout: 180000 },
      );
    };

    try {
      let r;
      if (f.size <= DIRECT_LIMIT) {
        // Small file → single-shot (robust on iOS Safari, no chunk state)
        r = await runDirect();
      } else {
        // Large file → chunked, with auto-restart if session expires
        try {
          r = await runChunked();
        } catch (err) {
          const status = err?.response?.status;
          if (status === 410 || status === 404) {
            toast.info("Verbindung unterbrochen – starte Upload neu …");
            setUploadProgress(0);
            r = await runChunked();
          } else {
            throw err;
          }
        }
      }
      setUploadProgress(100);

      setFileUrl(r.data.file_url);
      setFileName(r.data.file_name);
      setFileSize(r.data.file_size);
      setContentType(r.data.content_type);
      if (!title) setTitle((f.name || "").replace(/\.(pdf|docx?|xlsx?|png|jpg|jpeg|heic|heif|webp)$/i, ""));
      if (r.data.compressed && r.data.original_size) {
        const orig = (r.data.original_size / 1024 / 1024).toFixed(1);
        const now = (r.data.file_size / 1024 / 1024).toFixed(1);
        const pct = Math.round((1 - r.data.file_size / r.data.original_size) * 100);
        toast.success(`Datei hochgeladen & komprimiert: ${orig} MB → ${now} MB (${pct}% kleiner)`);
      } else {
        const size = (r.data.file_size / 1024 / 1024).toFixed(1);
        toast.success(`Datei hochgeladen (${size} MB)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Reset input so selecting the SAME file again re-triggers onChange (iOS quirk)
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async () => {
    if (!title.trim()) return toast.error("Titel fehlt");
    if (!fileUrl) return toast.error("Bitte Datei hochladen");
    setSaving(true);
    try {
      const body = {
        title: title.trim(), description: description.trim(),
        file_url: fileUrl, file_name: fileName, file_size: fileSize, content_type: contentType,
        category, order: parseInt(order || 0), published,
      };
      if (initial?.id) {
        await api.put(`/admin/downloads/${initial.id}`, body);
      } else {
        await api.post("/admin/downloads", body);
      }
      toast.success("Gespeichert");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ backgroundColor: "rgba(26,47,28,0.6)" }} onClick={onClose} data-testid="downloads-dialog">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--wp-border)" }}>
          <h3 className="font-display text-xl">{initial ? "Download bearbeiten" : "Neuer Download"}</h3>
          <button onClick={onClose} className="opacity-60 hover:opacity-100">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <label className="block">
            <div className="overline mb-1">Titel</div>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} data-testid="downloads-form-title" />
          </label>
          <label className="block">
            <div className="overline mb-1">Beschreibung (optional)</div>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="overline mb-1">Kategorie</div>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full px-3 py-2 rounded-lg border bg-white" style={{ borderColor: "var(--wp-border)" }} data-testid="downloads-form-category">
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="overline mb-1">Reihenfolge</div>
              <input type="number" value={order} onChange={(e) => setOrder(e.target.value)} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} data-testid="downloads-form-published" />
            <span>Öffentlich sichtbar</span>
          </label>

          <div className="p-3 rounded-xl border" style={{ borderColor: "var(--wp-border)", backgroundColor: "var(--wp-cream)" }}>
            <div className="overline mb-2">Datei</div>
            <input ref={fileRef} type="file" onChange={onFile} accept=".pdf,.docx,.doc,.xlsx,.xls,.png,.jpg,.jpeg,.heic,.heif,.webp,image/*,application/pdf" className="hidden" data-testid="downloads-form-file" />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="btn-secondary text-sm"
              data-testid="downloads-form-file-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {fileUrl ? "Datei ersetzen" : "Datei hochladen"}
            </button>
            <div className="text-[10px] opacity-60 mt-1">
              Max 500 MB · Große PDFs werden in 4-MB-Stücken hochgeladen und automatisch komprimiert
            </div>
            {uploading && uploadProgress > 0 && (
              <div className="mt-2">
                <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-[var(--wp-primary)] transition-all" style={{ width: `${uploadProgress}%` }} />
                </div>
                <div className="text-[10px] opacity-60 mt-1">{uploadProgress < 92 ? `Chunks hochladen … ${uploadProgress}%` : "Komprimiere & speichere …"}</div>
              </div>
            )}
            {fileUrl && (
              <div className="text-xs opacity-70 mt-2 flex items-center gap-2">
                <FileText className="w-3.5 h-3.5" /> {fileName} {fileSize ? `(${(fileSize/1024).toFixed(0)} KB)` : ""}
                <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="underline ml-auto">öffnen ↗</a>
              </div>
            )}
          </div>
        </div>
        <div className="px-5 py-3 border-t flex items-center gap-2 justify-end" style={{ borderColor: "var(--wp-border)" }}>
          <button onClick={onClose} className="btn-secondary text-sm">Abbrechen</button>
          <button onClick={submit} disabled={saving || uploading} className="btn-primary" data-testid="downloads-form-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Speichern
          </button>
        </div>
      </div>
    </div>
  );
}
